import { service } from '@ember/service'
import Component from '@glimmer/component'
import { colors } from '@colors'
import { t, type IntlService } from 'ember-intl'
import type { AxisLabelsFormatterContextObject, Point, SeriesBarOptions, XAxisTitleOptions } from 'highcharts'
import {
  SeriesColumn,
  type ChartSpacing,
  type SeriesColumnOptions,
} from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBox } from 'teacher-ui-client/components/giraffe/summary-box'
import { SeriesBar } from 'teacher-ui-client/components/giraffe/primitives/series-bar'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import { array } from '@ember/helper'
import formatValue from 'teacher-ui-client/helpers/format-value'

interface Lesson {
  lesson: number
  average_score: number
  student_count: number
  attempts: number
}

export type QuizScoresAveragesDataItem = {
  grade_position: number
  summary: {
    average_attempts: number
    average_score: number
    scores: [
      {
        range: '0-49'
        student_count: number
      },
      {
        range: '50-79'
        student_count: number
      },
      {
        range: '80-100'
        student_count: number
      },
    ]
  }
  lessons: Lesson[]
}

interface Args {
  data: QuizScoresAveragesDataItem
  grade: string
  title?: string
  tooltip?: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class QuizScoresAverages extends Component<Signature> {
  @service intl!: IntlService

  readonly chartSpacing: ChartSpacing = [10, 10, 10, 10]

  readonly colorScheme = SummaryBoxColorScheme.DarkBlue

  readonly summaryChartXAxisTitle: XAxisTitleOptions = {
    text: 'Scores %',
    align: 'high',
    rotation: 0,
    offset: 0,
  }

  get scores() {
    return [...this.args.data.summary.scores].sort(
      (a, b) => parseInt(b.range.split('-')[0]) - parseInt(a.range.split('-')[0]),
    )
  }

  get maxStudentCount() {
    const studentCounts = this.scores.map((item) => item.student_count)
    return studentCounts.reduce((max, currentValue) => {
      return Math.max(max, currentValue)
    }, studentCounts[0])
  }

  get summaryChartColors() {
    return [colors.grapeyGreen300, colors.yolkyYellow300, colors.watermelonyRed300]
  }

  yAxisLabelFormatter(this: AxisLabelsFormatterContextObject) {
    return `${this.value}%`
  }

  get lessons() {
    return [...(this.args.data.lessons ?? [])].sort((a, b) => a.lesson - b.lesson)
  }

  get toolTipFormatter() {
    const intl = this.intl
    return function (this: Point) {
      const { lesson } = this as Point & { lesson: Lesson }
      const { average_score: score, student_count: studentCount, attempts } = lesson

      const studentCountLabel = intl.t('reporting.statBoxes.assessmentScoresTotals.studentCount')

      return `Attempts: ${attempts}<br>Score: ${score}%<br>${studentCountLabel}: ${studentCount}`
    }
  }

  get summaryChartData(): SeriesBarOptions[] {
    const data = this.scores.map((score, index) => {
      return { x: index, y: score.student_count, score }
    })
    return [{ name: 'student_counts', data, type: 'bar' }]
  }

  get chartData(): SeriesColumnOptions[] {
    const data = this.lessons.map((lesson, index) => {
      return { x: index, y: lesson.average_score, lesson }
    })
    return [{ name: 'average_scores', color: colors.wlBlue250, data, type: 'column' }]
  }

  get summaryChartCategories() {
    return this.scores.map((item) => item.range)
  }

  get chartCategories() {
    return this.lessons.map((item) => item.lesson.toString())
  }

  <template>
    <div data-test-giraffe-quiz-scores-averages class="h-full" ...attributes>
      <ChartInfoTooltip @tooltipContent={{@tooltip}} />
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title="{{t 'reporting.charts.quizScores.averageScore'}}: {{formatValue
              @data.summary.average_score
              append="%"
            }}"
            @grade={{@grade}}
            @colorScheme={{this.colorScheme}}
          >
            <SeriesBar
              @data={{this.summaryChartData}}
              @categories={{this.summaryChartCategories}}
              @colors={{this.summaryChartColors}}
              @colorByPoint={{true}}
              @plotBackgroundColor="transparent"
              @xAxisLabel={{this.summaryChartXAxisTitle}}
              @yRange={{array 0 this.maxStudentCount}}
              @yAxisLabel={{t "reporting.statBoxes.assessmentScoresTotals.numOfStudents"}}
            />
          </SummaryBox>
        </:left>
        <:right>
          <SeriesColumn
            @data={{this.chartData}}
            @categories={{this.chartCategories}}
            @title={{@title}}
            @toolTipFormatter={{this.toolTipFormatter}}
            @yAxisLabelFormatter={{this.yAxisLabelFormatter}}
            @chartSpacing={{this.chartSpacing}}
            @xAxisLabel="Lesson"
            @yRange={{array 0 100}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default QuizScoresAverages
